import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, CircularProgress, Paper, Divider } from '@mui/material';
import { EmojiEvents, TrendingUp } from '@mui/icons-material';
import { idoraforditasService, TopWorkerDto } from '../services/idoraforditasService';

const TopWorkersWidget: React.FC = () => {
  const [weeklyTopWorkers, setWeeklyTopWorkers] = useState<TopWorkerDto[]>([]);
  const [monthlyTopWorkers, setMonthlyTopWorkers] = useState<TopWorkerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTopWorkers = async () => {
      try {
        setLoading(true);
        const [weeklyData, monthlyData] = await Promise.all([
          idoraforditasService.getTopWorkersForCurrentWeek(3),
          idoraforditasService.getTopWorkersForCurrentMonth(3)
        ]);
        setWeeklyTopWorkers(weeklyData);
        setMonthlyTopWorkers(monthlyData);
        setError(null);
      } catch (err) {
        console.error('Hiba a top dolgozók lekérdezése során:', err);
        setError('Nem sikerült betölteni az adatokat');
      } finally {
        setLoading(false);
      }
    };

    fetchTopWorkers();
    // Frissítés 5 percenként
    const interval = setInterval(fetchTopWorkers, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getMedalColor = (rank: number): string => {
    switch (rank) {
      case 1:
        return '#FFD700'; // Arany
      case 2:
        return '#C0C0C0'; // Ezüst
      case 3:
        return '#CD7F32'; // Bronz
      default:
        return 'rgba(255, 255, 255, 0.5)';
    }
  };

  const renderWorkersList = (workers: TopWorkerDto[], title: string, icon: React.ReactNode) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>
      <List dense sx={{ p: 0 }}>
        {workers.map((worker) => (
          <ListItem
            key={worker.userId}
            sx={{
              px: 1,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                minWidth: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: getMedalColor(worker.rank),
                color: '#000',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                mr: 1.5,
              }}
            >
              {worker.rank}
            </Box>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {worker.userName}
                </Typography>
              }
              sx={{ flex: 1, m: 0 }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: 'rgba(255, 255, 255, 0.9)',
                ml: 1,
              }}
            >
              {worker.totalHours.toFixed(1)} óra
            </Typography>
          </ListItem>
        ))}
      </List>
      {workers.length === 0 && (
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', textAlign: 'center', py: 1 }}>
          Nincs adat
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        color: 'white',
        p: 2,
        m: 2,
        borderRadius: 2,
      }}
    >
      {renderWorkersList(
        weeklyTopWorkers,
        'Heti rangsor',
        <TrendingUp sx={{ mr: 1, color: '#4CAF50' }} />
      )}
      
      <Divider sx={{ my: 2, backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
      
      {renderWorkersList(
        monthlyTopWorkers,
        'Havi rangsor',
        <EmojiEvents sx={{ mr: 1, color: '#FFD700' }} />
      )}
    </Paper>
  );
};

export default TopWorkersWidget;
